import {useEffect, useRef, useState} from 'react';
import AurButton from '../common/aur-button/AurButton';
import {conciergeReportFileExists} from '../../api/ReportingManagement';
import AurPopup from '../common/aur-popup/AurPopup';
import {t} from 'i18next';
import {CircularProgress, Typography} from '@mui/material';
import AurIcon, {IconColors, IconNames} from '../common/aur-icon/AurIcon';
import {ISelectItem} from '../common/aur-select-dropdown/types';
import FileRenameBox from './FileRenameBox';
import {useToast} from '../../contexts/toastContext/ToastContext';
import {
  CONCIERGE_FORM_TYPE,
  CONCIERGE_REPORTS_ACCEPT_FILE,
} from '../../constants/constants';
import AurLoadingIndicator from '../common/aur-loading-indicator/AurLoadingIndictor';
import {UploadDocumentInterface} from '../../types/concierge-reports/conciergeReportsType';
import {getFileExtension, isValidFileName} from '../../utils/utils';
import {AxiosError} from 'axios';

type propsTypes = {
  handleSelect: (file: File, fileName: string) => void;
  client: ISelectItem;
  project: ISelectItem;
  selectedTeamMembers: ISelectItem[];
  currentStep: string;
  changeMade: boolean;
  uploadDocumentData: UploadDocumentInterface;
};

/**
 * Constant for Errors
 */
const typographyValues = 'conciergeReports.file';

/**
 * AurIcon for heading icon in modal
 */
const errorIcon = (
  <AurIcon icon={IconNames.Error} color={IconColors.Critical} />
);

/**
 * Modal body to display content & filename
 * @param param0
 * @returns
 */
const PopupModalBody = ({
  errorKey,
  fileName,
}: {
  errorKey: string;
  fileName: string | undefined;
}) => {
  return (
    <>
      <Typography className={`body2_size content - text`}>
        {t(`${typographyValues}.errors.${errorKey}.content`)}
      </Typography>
      <Typography className="typo-text subhead3_size content-text file-name">
        {fileName}
      </Typography>
    </>
  );
};

/**
 * Component to select file for concierge reports
 * @param props
 * @returns
 */
const SelectFile = (props: propsTypes) => {
  const {
    handleSelect,
    client,
    project,
    selectedTeamMembers,
    currentStep,
    changeMade,
    uploadDocumentData,
  } = props;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [updateBtnDisabled, setUpdateBtnDisabled] = useState<boolean>(true);
  const [renameModal, setRenameModal] = useState<boolean>(false);
  const [renamedFile, setRenamedFile] = useState<string>('');
  const [error, setError] = useState<boolean>(false);
  const [errorType, setErrorType] = useState<string>('');
  const [errorModal, setErrorModal] = useState<boolean>(false);
  const [isFileLoading, setIsFileLoading] = useState<boolean>(false);
  const [showButtonLoader, setShowButtonLoader] = useState<boolean>(false);
  const {showToast} = useToast();

  useEffect(() => {
    file && validationHandler(file);
  }, [file]);

  useEffect(() => {
    if (!error && file) {
      uploadReportFile(file, renamedFile);
    }
  }, [error]);

  /**
   * Handler for Save Changes button
   */
  const handleSaveClick = () => {
    uploadReportFile(uploadDocumentData.file, uploadDocumentData.fileName);
  };

  /**
   * Handler Select File button
   */
  const handleSelectFileClick = () => {
    // Trigger file input click for upload
    fileInputRef.current?.click();
    setFile(null);
  };

  /**
   * Handler for File Change
   */
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      setFile(files[0]);
      e.target.value = '';
    }
  };

  /**
   * Reset All State
   */
  const resetAllState = () => {
    setFile(null);
    setUpdateBtnDisabled(true);
    setRenameModal(false);
    setRenamedFile('');
    setError(false);
    setErrorType('');
    setErrorModal(false);
    setIsFileLoading(false);
    setShowButtonLoader(false);
  };

  /**
   * Vadidation Handler for file
   * @param file
   */
  const validationHandler = (file: File) => {
    const fileExtension = '.' + getFileExtension(file.name);
    const maxAllowedSize: number = 10 * 1024 * 1024;
    if (!isValidFileName(file.name)) {
      setError(true);
      setErrorModal(true);
      setErrorType('unSupportedFileName');
    } else if (Number(file?.size) > maxAllowedSize) {
      setError(true);
      setErrorModal(true);
      setErrorType('largeSize');
    } else if (file && !CONCIERGE_REPORTS_ACCEPT_FILE.includes(fileExtension)) {
      setError(true);
      setErrorModal(true);
      setErrorType('unSupported');
    } else {
      uploadReportFile(file, renamedFile);
    }
  };

  /**
   * API integration to validate file
   * @param file
   * @param renamedFile
   */
  const uploadReportFile = async (file: File, renamedFile: string | null) => {
    if (!error) {
      const fileName = renamedFile || file.name;
      setIsFileLoading(true);
      setShowButtonLoader(false);
      try {
        await conciergeReportFileExists({fileName});
        handleSelect(file, fileName);
        setIsFileLoading(false);
        setShowButtonLoader(true);
      } catch (error: unknown) {
        const err = error as AxiosError;
        const errorCode = err.status;
        if (errorCode == 409) {
          setError(true);
          setErrorModal(true);
          setErrorType('alreadyExists');
        } else {
          resetAllState();
          showToast(err.message, 'error', undefined, 'center');
        }
        setIsFileLoading(false);
        setShowButtonLoader(false);
      }
    }
  };

  /**
   * Handler to match file name with renamed file and disable update button
   * @param name renamed file name
   */
  const handleFileName = (name: string) => {
    if (isValidFileName(name)) {
      if (file?.name && name && name !== file?.name)
        setUpdateBtnDisabled(false);
      else setUpdateBtnDisabled(true);
    } else {
      setUpdateBtnDisabled(true);
    }
    setRenamedFile(name);
  };

  /**
   * Handler to update file name
   */
  const handleUpdate = () => {
    setRenameModal(false);
    setUpdateBtnDisabled(true);
    setError(false);
  };

  /**
   * New file upload modal handler
   */
  const handleModalUploadNew = () => {
    setErrorModal(false);
    setError(false);
    setErrorType('');
    setRenamedFile('');
    handleSelectFileClick();
  };

  /**
   * Modal cancel handler
   */
  const handleModalCancel = () => {
    setErrorModal(false);
    setFile(null);
    setError(false);
    setErrorType('');
  };

  /**
   * Rename Modal cancel handler
   */
  const handleRenameModalCancel = () => {
    setRenameModal(false);
    setRenamedFile('');
    setError(false);
    setFile(null);
    setUpdateBtnDisabled(true);
  };

  return (
    <>
      {currentStep == CONCIERGE_FORM_TYPE.UPLOAD ? (
        <>
          {isFileLoading && (
            <CircularProgress
              size={64}
              thickness={5}
              className="loader-trasparent"
            />
          )}
          <AurButton
            classNames="drawer-close-button panel-close"
            label={t('conciergeReports.buttonsLabels.selectFile')}
            disabled={!(client && project && selectedTeamMembers.length > 0)}
            type="continue"
            size="Medium"
            title={t('conciergeReports.buttonsLabels.selectFile')}
            onClick={handleSelectFileClick}
          />
        </>
      ) : (
        <AurButton
          type="continue"
          size="Medium"
          disabled={
            !(client && project && selectedTeamMembers.length > 0 && changeMade)
          }
          classNames="drawer-close-button panel-close"
          label={t('conciergeReports.buttonsLabels.saveChanges')}
          startIcon={
            showButtonLoader ? (
              <AurLoadingIndicator size={24} className="progress-icon" />
            ) : (
              ''
            )
          }
          onClick={handleSaveClick}
        />
      )}

      {/* Input file with ref */}
      <input
        ref={fileInputRef}
        style={{display: 'none'}}
        data-testid="file-input"
        type="file"
        onChange={handleFileChange}
        accept={CONCIERGE_REPORTS_ACCEPT_FILE}
      />

      {/* Unsupported file error */}
      {errorType === 'unSupported' && (
        <AurPopup
          ariaLabel={t(
            `${typographyValues}.errors.unSupported.ariaDescription`
          )}
          className="inactivity-popup-modal conciergeReports-errorUpload-popup"
          open={errorModal}
          onOk={handleModalUploadNew}
          onCancel={handleModalCancel}
          popupHeading={t(`${typographyValues}.errors.unSupported.title`)}
          showPopupHeading={true}
          showCloseIcon={false}
          contentType="element"
          popupContent={
            <PopupModalBody errorKey={errorType} fileName={file?.name} />
          }
          labelActionCancel={t('common.errorUploadModal.buttonStay')}
          labelActionOk={t(`${typographyValues}.buttonLabels.uploadNewReport`)}
          headingIcon={errorIcon}
        />
      )}

      {/* Large Size file error */}
      {errorType === 'largeSize' && (
        <AurPopup
          ariaLabel={t(`${typographyValues}.errors.largeSize.ariaDescription`)}
          className="inactivity-popup-modal conciergeReports-errorUpload-popup"
          open={errorModal}
          onOk={handleModalUploadNew}
          onCancel={handleModalCancel}
          popupHeading={t(`${typographyValues}.errors.largeSize.title`)}
          showPopupHeading={true}
          showCloseIcon={false}
          contentType="element"
          popupContent={
            <PopupModalBody errorKey={errorType} fileName={file?.name} />
          }
          labelActionCancel={t('common.errorUploadModal.buttonStay')}
          labelActionOk={t(`${typographyValues}.buttonLabels.uploadNewReport`)}
          headingIcon={errorIcon}
        />
      )}

      {/* Already exists file error */}
      {errorType === 'alreadyExists' && (
        <AurPopup
          ariaLabel={t(
            `${typographyValues}.errors.alreadyExists.ariaDescription`
          )}
          className="inactivity-popup-modal conciergeReports-errorUpload-popup"
          open={errorModal}
          onOk={handleModalUploadNew}
          onCancel={() => {
            setRenameModal(true);
            setErrorModal(false);
          }}
          popupHeading={t(`${typographyValues}.errors.alreadyExists.title`)}
          showPopupHeading={true}
          showCloseIcon={false}
          contentType="element"
          popupContent={
            <PopupModalBody
              errorKey={errorType}
              fileName={renamedFile || file?.name}
            />
          }
          labelActionCancel={t(`${typographyValues}.buttonLabels.renameReport`)}
          labelActionOk={t(`${typographyValues}.buttonLabels.uploadNewReport`)}
          headingIcon={errorIcon}
        />
      )}

      {/* Rename file modal  */}
      <AurPopup
        open={renameModal}
        showPopupHeading={true}
        popupHeading={t('conciergeReports.file.rename.title')}
        popupContent={
          <FileRenameBox
            handleFileName={handleFileName}
            fileName={renamedFile || file?.name || ''}
          />
        }
        labelActionOk={t(`${typographyValues}.buttonLabels.update`)}
        labelActionCancel={t('common.errorUploadModal.buttonStay')}
        continueButtonState={updateBtnDisabled}
        onCancel={handleRenameModalCancel}
        onOk={handleUpdate}
        contentType="element"
        className="inactivity-popup-modal conciergeReports-errorUpload-popup"
      />

      {/* Unsupported file name modal  */}
      {errorType === 'unSupportedFileName' && (
        <AurPopup
          ariaLabel={t(
            `${typographyValues}.errors.unSupportedFileName.ariaDescription`
          )}
          className="inactivity-popup-modal conciergeReports-errorUpload-popup"
          open={errorModal}
          onOk={() => {
            setRenameModal(true);
            setErrorModal(false);
          }}
          onCancel={handleModalCancel}
          popupHeading={t(
            `${typographyValues}.errors.unSupportedFileName.title`
          )}
          showPopupHeading={true}
          showCloseIcon={false}
          contentType="element"
          popupContent={
            <PopupModalBody
              errorKey={errorType}
              fileName={renamedFile || file?.name}
            />
          }
          labelActionCancel={t('common.errorUploadModal.buttonStay')}
          labelActionOk={t(`${typographyValues}.buttonLabels.renameReport`)}
          headingIcon={errorIcon}
        />
      )}
    </>
  );
};

export default SelectFile;
